import { useMemo } from 'react';

import type { StateWithoutError } from 'src/app/shared/utils/async';
import { isLoading } from 'src/app/shared/utils/async';

import type { ShareChannel, ShareConfig } from '../shared/types';

import { useAsyncAvailableShareChannels } from './async';
import { useBaseAvailableShareChannels } from './base';
import { useSyncAvailableShareChannels } from './sync';

export function useShareChannelsAvailability(config: ShareConfig): Readonly<Record<ShareChannel, boolean>> {
	const base = useBaseAvailableShareChannels(config);
	const sync = useSyncAvailableShareChannels(config);

	return useMemo(
		() => ({
			...base,
			...sync,
		}),
		[base, sync],
	);
}

type AsyncResult = StateWithoutError<{ channels: Readonly<Record<ShareChannel, boolean>> }>;

export function useShareChannelsAvailabilityAsync(config: ShareConfig): AsyncResult {
	const base = useBaseAvailableShareChannels(config);
	const async = useAsyncAvailableShareChannels(config);

	return useMemo(() => {
		if (isLoading(async)) return async;

		return {
			status: 'loaded',
			channels: {
				...base,
				...async.channels,
			},
		};
	}, [base, async]);
}
